import Vue from 'vue'
import store from './store'
import axios from 'axios'
import JsCookies from 'js-cookie'

Vue.prototype.$imgUrl = (path) => {
	return Vue.prototype.$apiUrl + '/storage/' + path
}

Vue.prototype.$metric = (metric, native) => {
	if (!native && !store.state.client.no_analytics) {
		window.dataLayer = window.dataLayer || []
		window.dataLayer.push({
			...metric,
			end_user_id: store.state.endUser?.id,
		})
	}

	// Native metrics/analytics
	axios.post(Vue.prototype.$apiUrl + '/api/metric', {
		metric,
		user_agent: window.navigator.userAgent,
		end_user_id: store.state.endUser?.id
	})
}

/**
 * @param {Object} event - Event object to track
 * @param {number} [event.event_type_id] - Event type identifier
 * @param {number} [event.end_user_id] - End user identifier
 * @param {number} [event.campaign_id] - Campaign identifier
 * @param {number} [event.client_id] - Client identifier
 * @param {number} [event.survey_id] - Survey identifier
 * @param {string} [event.value] - Event value
 */
Vue.prototype.$event = (event) => {
	if (!store.state.client.no_analytics) {
		window.dataLayer = window.dataLayer || []
		window.dataLayer.push({
			event: 'nudgd_event',
			nudgd_event: event,
			end_user_id: store.state.endUser?.id
		})
	}
	axios.post(Vue.prototype.$apiUrl + '/api/event', { event })
}

Vue.prototype.__ = (slug, fallbackString) => {
	return Vue.prototype.$processText((store.getters.translations && store.getters.translations[slug]) || fallbackString || '')
}

// Optional chaining / null coalesce
Vue.prototype.$oc = (object, property) => {
	return object ? object[property] : null
}

// Global cookie setting (try to set)
Vue.prototype.$setCookie = (key, value, expire) => {
	// Not allowed on client with ID = 27
	if (store.state.client.id !== 27) {
		if (expire) {
			return JsCookies.set(key, value, {expires: expire})
		} else {
			return JsCookies.set(key, value)
		}
	}
}

Vue.prototype.$deleteCookie = (key) => {
	JsCookies.remove(key)
}

// Global cookie getting
Vue.prototype.$getCookie = (key) => {
	return JsCookies.get(key)
}

Vue.prototype.$changeLang = (lang) => {
	if (!store.state.endUser.slug) {
		store.state.globalLanguage = lang
		axios.get(Vue.prototype.$apiUrl + '/api/language/' + lang).then(res => {
			store.state.translations = res.data.language
			// For non-logged in users, we'll need to refresh the entire app state
			window.location.reload()
		})
	} else {
		// If end user is present
		if (store.state.endUser.language === lang) {
			return
		}

		store.state.endUser.language = lang
		let slug = store.state.endUser.slug

		// First update the language preference
		axios.post(Vue.prototype.$apiUrl + '/api/end-user/' + slug + '/update-language', {lang}).then(res => {
			store.state.translations = res.data.translations

			// Then fetch fresh data including cards in the new language
			axios.get(Vue.prototype.$apiUrl + '/api/end-user/' + slug + '?appHost=' + window.location.hostname).then(userRes => {
				// Use the store's setup mutation to properly initialize the new data
				store.commit('setup', userRes.data)
				// Force a re-render of components that use calculatorRerender as key
				store.state.calculatorRerender = Date.now()
			}).catch(error => {
				console.error('Error fetching user data:', error)
			})
		}).catch(error => {
			console.error('Error updating language:', error)
		})
	}
}

function getAverageSurveyValue(key) {
  const responses = store.state.surveyResponses || [];

  const values = responses
    .map(r => r.response?.[key])
    .filter(v => v !== undefined && !isNaN(v));

  if (!values.length) return undefined;

  const avg = values.reduce((a, b) => Number(a) + Number(b), 0) / values.length;
  return avg;
}

// Process text (used all over the place)
Vue.prototype.$processText = (html) => {
	if ( ! html) {
		return html
	}

	// Add rel to text
	let whiteList = ['([^/]+.)?nagotsomintefinns.com']
	let str = '(<as*(?!.*\brel=)[^>]*)(href="https?://)((?!(?:' + whiteList.join('|') + '))[^"]+)"((?!.*\brel=)[^>]*)(?:[^>]*)>'

	html = html.replace(new RegExp(str, 'igm'), '$1$2$3"$4 rel="noopener noreferrer nofollow" target="_blank">')

	// Switch out variables
	if (html.includes('{firstName}')) {
		if (store.state.endUser.first_name) {
			html = html.replace('{firstName}', store.state.endUser.first_name)
		} else {
			let regex = new RegExp(/\n?\s?{firstName}\n?/gm)
			html = html.replace(regex, '')
		}
	}

	if (html.includes('{zipCode}')) {
		if (store.state.endUser.zip) {
			html = html.replace('{zipCode}', store.state.endUser.zip)
		} else if (store.state.client?.customization?.default_zip) {
			html = html.replace('{zipCode}', store.state.client.customization.default_zip)
		} else {
			let regex = new RegExp(/\n?\s?{zipCode}\n?/gm)
			html = html.replace(regex, '')
		}
	}

	if (html.includes('{slug}')) {
		if (store.state.endUser.slug) {
			html = html.replace('{slug}', store.state.endUser.slug)
		} else {
			let regex = new RegExp(/\n?\s?{slug}\n?/gm)
			html = html.replace(regex, '')
		}
	}

	if (html.includes('{clientName}')) {
		if (store.state.client.title) {
			html = html.replace('{clientName}', store.state.client.title)
		} else {
			let regex = new RegExp(/\n?\s?{clientName}\n?/gm)
			html = html.replace(regex, '')
		}
	}

  const keysToReplace = ['tenants', 'household_may', 'average_may'];

  for (const key of keysToReplace) {
    const pattern = new RegExp(`{${key}}`, 'g');
    const avg = getAverageSurveyValue(key);
    if (avg !== undefined) {
      html = html.replace(pattern, Math.round(avg));
    }
  }

	// Switch out variables from tag shortcodes & calculator shortcodes
	for (let shortcode of html.match(/\{(.*?)\}/g) || []) {
		let matchingShortcode = store.getters.shortcodes.find(s => s.shortcode === shortcode.substring(1, shortcode.length - 1))
		let matchingCalculatorShortcode = store.state.parsedCalculatorShortcodes[shortcode.substring(1, shortcode.length - 1)] || null

		if (matchingShortcode) {
			html = html.replace(shortcode, matchingShortcode.value)
		} else if (matchingCalculatorShortcode) {
			html = html.replace(shortcode, matchingCalculatorShortcode)
		} else {
			let regex = new RegExp(`\\n?\\s?${shortcode}(?!{)\\n?`, 'gm')
			html = html.replace(regex, '')

			let regex2 = new RegExp(`\\n?${shortcode}\\n?`, 'gm')
			html = html.replace(regex2, '')
		}
	}

	return html
}

Vue.prototype.$processAsyncText = async (html) => {
  if (!html) return html;

  // Handle math expressions: {calculated value = 9930:household_may / 2131:tenants}
  const mathRegex = /\{calculated value\s*=\s*(\d+):([\w_]+)\s*([+\-*/])\s*(\d+):([\w_]+)\}/g;
  const mathMatches = [...html.matchAll(mathRegex)];

  for (const [fullMatch, cardId1, key1, operator, cardId2, key2] of mathMatches) {
    try {
      const [res1, res2] = await Promise.all([
        axios.get(Vue.prototype.$apiUrl + '/api/survey/responses', {
          params: {
            cardId: Number(cardId1),
            clientId: store.state.client.id,
            userId: store.state.endUser.id,
          },
        }),
        axios.get(Vue.prototype.$apiUrl + '/api/survey/responses', {
          params: {
            cardId: Number(cardId2),
            clientId: store.state.client.id,
            userId: store.state.endUser.id,
          },
        }),
      ]);

      const val1 = +res1.data?.[0]?.response?.[key1];
      let val2 = +res2.data?.[0]?.response?.[key2];

      if (val1 === undefined) continue;

      if (operator === '/' && (!val2 || isNaN(val2) || Number(val2) === 0)) {
        console.warn(`[calc] ${key2} missing or invalid, defaulting to 1`);
        val2 = 1;
      }

      let result;
      switch (operator) {
        case '+': result = Number(val1) + Number(val2); break;
        case '-': result = Number(val1) - Number(val2); break;
        case '*': result = Number(val1) * Number(val2); break;
        case '/': result = Number(val1) / Number(val2); break;
      }

      html = html.replace(fullMatch, `<strong>${Math.round(result)}</strong>`);
    } catch (err) {
      console.warn('Error parsing or fetching values for calculated shortcode:', fullMatch, err);
      html = html.replace(fullMatch, '–');
    }
  }

  // Handle simple replacements: {2131:answer}
  const simpleRegex = /\{(\d+):([\w_]+)\}/g;
  const simpleMatches = [...html.matchAll(simpleRegex)];

  for (const [fullMatch, cardId, key] of simpleMatches) {
    try {
      const res = await axios.get(Vue.prototype.$apiUrl + '/api/survey/responses', {
        params: {
          cardId: Number(cardId),
          clientId: store.state.client.id,
          userId: store.state.endUser.id,
        },
      });

      const value = res.data?.[0]?.response?.[key];

      if (value !== undefined) {
        html = html.replace(fullMatch, `<strong>${value}</strong>`);
      } else {
        html = html.replace(fullMatch, '–');
      }
    } catch (err) {
      console.warn('Failed to fetch value for', fullMatch, err);
      html = html.replace(fullMatch, '–');
    }
  }

  return html;
};
